/**
 * CreditsPage.js — Public, paginated index of every Wikipedia-licensed
 * photo we host. Required by CC BY-SA reuse policy + recommended by
 * Wikimedia Commons "reusing content outside Wikimedia" guidance:
 *
 *   "If used online, the credit line must include hyperlinks to the
 *    original file's page on Wikimedia Commons and to the licence."
 *
 * We render the credit inline next to a small avatar so it's clear
 * which photo each attribution covers. AI-generated portraits and any
 * future commercial-licensed photos do not appear here (no attribution
 * required).
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PhotoAttribution from '../components/PhotoAttribution';

const PAGE_SIZE = 50;

export default function CreditsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/credits', {
        params: { page, page_size: PAGE_SIZE, q },
      });
      setData(res.data || { items: [], total: 0 });
    } catch (e) {
      console.error(e);
      setData({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, q]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-background" data-testid="credits-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="mb-4"
          data-testid="credits-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>

        <header className="mb-6">
          <h1
            className="text-2xl sm:text-3xl font-bold"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Photo credits
          </h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Photographs of public-figures on Fantasy World Star are
            sourced from <a
              href="https://commons.wikimedia.org/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="underline hover:text-foreground"
            >Wikimedia Commons</a> under their original Creative Commons
            licences (CC BY-SA, CC BY, Public Domain, etc.). Each photo
            is credited to its photographer below, with a link back to
            the original file page on Commons and the full licence text.
            Stylised portraits of athletes, musicians, and actors are
            AI-generated and carry no Commons attribution.
          </p>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Want a photo removed?{' '}
            <a
              href="/legal/takedown"
              className="underline hover:text-foreground"
            >Submit a takedown request</a>.
          </p>
        </header>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Filter by name…"
            className="pl-9"
            data-testid="credits-search"
          />
        </div>

        <p className="text-xs text-muted-foreground mb-3">
          {loading ? 'Loading…' : `${data.total} photo${data.total === 1 ? '' : 's'}`}
        </p>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-12">
            No credits to display.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="credits-list">
            {data.items.map((row) => (
              <li
                key={row.entity_id}
                className="bg-card border border-border/60 rounded-xl p-3 flex gap-3"
                data-testid={`credit-row-${row.entity_id}`}
              >
                {row.thumbnail ? (
                  <img
                    src={row.thumbnail}
                    alt=""
                    loading="lazy"
                    className="h-12 w-12 rounded-lg object-cover bg-muted shrink-0"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-muted shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">{row.name}</p>
                    {row.entity_type && (
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wide">
                        {row.entity_type.replace(/_/g, ' ')}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1">
                    <PhotoAttribution attribution={row.attribution} compact />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="credits-prev-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Prev
            </Button>
            <p className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              data-testid="credits-next-btn"
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
